import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Bell,
  BellOff, 
  Plus, 
  ChevronDown,
  Filter,
  Database,
  Sparkles,
  ExternalLink,
  File,
  GripVertical,
  Trash2,
  Wand2,
  ChevronLeft,
  Loader2,
  Edit2,
  Check,
  Info,
  Tag,
  Zap,
  Settings2,
  X,
  FileText,
  Gavel,
  Calendar,
  Clock,
  Star
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Textarea } from "./components/ui/textarea";
import {
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
} from "./components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "./components/ui/tooltip";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "./components/ui/tabs";
import { TabsInCell, TabsInCellList, TabsInCellTrigger } from "./components/ui/tabs-in-cell-for-navigation";
import { Switch } from "./components/ui/switch";
import { motion, AnimatePresence, Reorder, LayoutGroup } from "framer-motion";
import { cn } from "./lib/utils";
import { useToast } from "./hooks/use-toast";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import apiClient, { getApiBaseUrl } from "./lib/api";
import React from "react";
import FilterInsightsModal from "./components/FilterInsightsModal";
import DismissModal from "./components/DismissModal";
import ChatWidget from "./components/ChatWidget";
import PulseCenter from "./components/PulseCenter";
import OnboardingModal from "./components/OnboardingModal";
import MunicipalityFilter from "./components/MunicipalityFilter";
import { useCategoriesStore } from "./stores/categoriesStore";
import { usePulsesFoundPeriodStore } from "./stores/pulsesFoundPeriodStore";
import { useCategories, useCreateCategory, useDeleteCategory, useUpdateCategory } from "./hooks/useCategories";

// Default categories are now fetched from the database
// No hardcoded categories - all categories come from the API

// Municipalities list
const MUNICIPALITIES = [
  { id: "12", label: "City of Toronto" },
  { id: "1", label: "City of Mississauga" },
  { id: "11", label: "City of Brampton" },
  { id: "9", label: "City of Markham" },
  { id: "3", label: "City of Vaughan" },
  { id: "4", label: "Town of Oakville" },
  { id: "20", label: "City of Burlington" },
  { id: "19", label: "City of Hamilton" },
  { id: "13", label: "City of Ottawa" },
  { id: "18", label: "City of Kitchener" },
  { id: "17", label: "City of Waterloo" },
  { id: "2", label: "City of Oshawa" },
  { id: "10", label: "City of Pickering" },
  { id: "22", label: "Town of Ajax" },
  { id: "23", label: "City of Barrie" },
  { id: "24", label: "Municipality of Clarington" },
];

// Regional bodies
const REGIONS = [
  { id: "6", label: "Region of Peel" },
  { id: "7", label: "York Region" },
  { id: "8", label: "Durham Region" },
  { id: "25", label: "Halton Region" },
  { id: "16", label: "Region of Waterloo" },
  { id: "26", label: "Niagara Region" },
  { id: "15", label: "Leeds and Grenville" },
];

// Provincial sources
const PROVINCIAL = [
  { id: "ontario-newsroom", label: "Ontario Newsroom" },
  { id: "ero", label: "Environmental Registry (ERO)" },
  { id: "olt", label: "Ontario Land Tribunal (OLT)" },
  { id: "mmah", label: "Ministry of Municipal Affairs" },
  { id: "mto", label: "Ministry of Transportation" },
  { id: "mecp", label: "Ministry of Environment" },
];

export default function Dashboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedPulseItem, setSelectedPulseItem] = useState<any | null>(null);
  // Alerts removed - using pulses instead
  // const [selectedAlert, setSelectedAlert] = useState<any | null>(null);
  const [dismissItem, setDismissItem] = useState<{id: number, type: 'insight' | 'alert', title: string} | null>(null);
  const [subscribedInsights, setSubscribedInsights] = useState<Set<number>>(new Set());
  const [showChatWidget, setShowChatWidget] = useState(false);
  const [activeChatInsight, setActiveChatInsight] = useState<any | null>(null);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  
  // Check onboarding status
  const { data: onboardingStatus } = useQuery({
    queryKey: ["onboarding", "status"],
    queryFn: async () => {
      try {
        return await apiClient.topics.getOnboardingStatus();
      } catch (error) {
        console.error('[Dashboard] Error fetching onboarding status:', error);
        return { has_completed_onboarding: true }; // Default to completed on error
      }
    },
    retry: false,
  });

  // Show onboarding modal if not completed
  useEffect(() => {
    if (onboardingStatus && !onboardingStatus.has_completed_onboarding) {
      setShowOnboardingModal(true);
    }
  }, [onboardingStatus]);
  
  // Use Zustand store for categories state
  const { 
    categories, 
    error: categoriesError,
    setActiveCategory, 
    reorderCategories,
    updateCategoryCounts,
    removeCategory: removeCategoryFromStore,
    updateCategory 
  } = useCategoriesStore();
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedLevels, setSelectedLevels] = useState<('municipal' | 'provincial' | 'regional')[]>([]);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[]>([]);
  const [selectedProvStreams, setSelectedProvStreams] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'created' | 'event_date'>('event_date'); // Sort by created_at or event/meeting date
  const [meetingFilter, setMeetingFilter] = useState<'7days' | '30days' | 'all' | 'past'>('all');
  const [provincialFilter, setProvincialFilter] = useState<string>('all');
  const [showNeedsReview, setShowNeedsReview] = useState(false); // When false, hide pulses with insight meaningful_change_check === 'Needs Review'
  const { period: pulsesFoundPeriod, setPeriod: setPulsesFoundPeriod } = usePulsesFoundPeriodStore();
  const navigate = useNavigate();

  useEffect(() => {
    console.log("[ShowNeedsReview] Dashboard mounted; toggle is in header next to Municipality filter");
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get municipality/source filter from URL (can be string for provincial sources or number for municipalities)
  const municipalityParam = searchParams.get('municipality');
  const selectedMunicipalityId = municipalityParam 
    ? (isNaN(Number(municipalityParam)) ? municipalityParam : Number(municipalityParam))
    : null;

  // Fetch combined dashboard data (includes stats, dashboard, and pinned). Counts respect Show Needs Review and pulsesFoundPeriod.
  const { data: pulseDashboardResponse } = useQuery({
    queryKey: ["pulses", "dashboard", "business", selectedMunicipalityId, showNeedsReview, pulsesFoundPeriod],
    queryFn: async () => {
      try {
        return await apiClient.pulses.getDashboard("business", {
          include_needs_review: showNeedsReview,
          period: pulsesFoundPeriod && pulsesFoundPeriod !== "all" ? pulsesFoundPeriod : undefined,
        });
      } catch (error) {
        console.error('[Dashboard] Error fetching pulse dashboard:', error);
        return {
          stats: {
            new_pulses_today: 0,
            new_pulses_top3_match: 0,
            new_pulses_purpose: "How much new intelligence did Cityscrape add to my workspace today, and how much of it matches what I care about most?",
            priority_items_count: 0,
            priority_next_date: null,
            priority_items_purpose: "What priority-matching items have upcoming dates soon, and what is the next key date I should be aware of?",
            high_impact_items_count: 0,
            high_impact_next_date: null,
            high_impact_purpose: "What items are most likely to affect feasibility, cost, permissions, or timelines soon?",
            relevance_percentage: "—%",
            relevance_top3_count: 0,
            relevance_purpose: "Is the feed aligned to what my workspace cares about, week to week?"
          },
          dashboard: {
            municipal: { title: "Municipal", total_new: 0, sources: [] },
            regional: { title: "Regional", total_new: 0, sources: [] },
            provincial: { title: "Provincial", total_new: 0, sources: [] }
          },
          pinned: []
        };
      }
    },
    staleTime: 60000,
    refetchOnWindowFocus: true,
  });

  // Extract stats and dashboard data from combined response
  const dashboardStats = pulseDashboardResponse?.stats;
  const rawPulseDashboardData = pulseDashboardResponse?.dashboard;

  // Filter dashboard data by selected municipality
  const pulseDashboardData = React.useMemo(() => {
    if (!rawPulseDashboardData || !selectedMunicipalityId) {
      return rawPulseDashboardData;
    }

    // Filter sources to only show the selected municipality
    const filterSources = (sources: any[]) => {
      return sources
        .map((source: any) => {
          // Check if source matches the selected municipality
          // Source ID might be municipality_id or name
          const matchesMunicipality = 
            source.id === selectedMunicipalityId.toString() ||
            source.id === selectedMunicipalityId ||
            source.municipality_id === selectedMunicipalityId;
          
          if (matchesMunicipality) {
            return source;
          }
          return null;
        })
        .filter((source: any) => source !== null);
    };

    return {
      municipal: {
        ...rawPulseDashboardData.municipal,
        sources: filterSources(rawPulseDashboardData.municipal?.sources || []),
        total_new: filterSources(rawPulseDashboardData.municipal?.sources || [])
          .reduce((sum: number, source: any) => sum + (source.total_new || 0), 0)
      },
      regional: {
        ...rawPulseDashboardData.regional,
        sources: filterSources(rawPulseDashboardData.regional?.sources || []),
        total_new: filterSources(rawPulseDashboardData.regional?.sources || [])
          .reduce((sum: number, source: any) => sum + (source.total_new || 0), 0)
      },
      provincial: rawPulseDashboardData.provincial // Provincial sources don't filter by municipality
    };
  }, [rawPulseDashboardData, selectedMunicipalityId]);

  // Fetch categories using our existing hook (for Zustand store sync)
  const { data: categoriesData, isLoading: categoriesLoading, refetch: refetchCategories } = useCategories();
  
  // Mutations for creating, updating, and deleting categories
  const createCategoryMutation = useCreateCategory();
  const updateCategoryMutation = useUpdateCategory();
  const deleteCategoryMutation = useDeleteCategory();
  
  // Check if there are active filters
  const hasActiveFilters = 
    selectedLevels.length > 0 ||
    selectedMunicipalities.length > 0 ||
    selectedProvStreams.length > 0 ||
    selectedRegions.length > 0 ||
    sortBy !== 'event_date';

  // Get active category (only from followed categories)
  const activeCategory = categories.find(cat => cat.active && cat.is_followed !== false);
  
  // Extract filter options from pulse dashboard data
  const filterOptions = React.useMemo(() => {
    if (!pulseDashboardData) {
      return {
        municipalities: [],
        provincialStreams: [],
        regions: []
      };
    }

    const municipalities = new Set<string>();
    const provincialStreams = new Set<string>();
    const regions = new Set<string>();

    // Extract from municipal sources
    if (pulseDashboardData.municipal?.sources) {
      pulseDashboardData.municipal.sources.forEach((source: any) => {
        if (source.name) {
          municipalities.add(source.name);
        }
      });
    }

    // Extract from regional sources
    if (pulseDashboardData.regional?.sources) {
      pulseDashboardData.regional.sources.forEach((source: any) => {
        if (source.name) {
          regions.add(source.name);
        }
      });
    }

    // Extract from provincial sources
    if (pulseDashboardData.provincial?.sources) {
      pulseDashboardData.provincial.sources.forEach((source: any) => {
        if (source.name) {
          provincialStreams.add(source.name);
        }
      });
    }

    return {
      municipalities: Array.from(municipalities).sort(),
      provincialStreams: Array.from(provincialStreams).sort(),
      regions: Array.from(regions).sort()
    };
  }, [pulseDashboardData]);

  // No longer needed - PulseCenter handles all pulse display
  // Filtering is handled by PulseCenter's own queries
  
  // Alerts removed - using pulses instead
  const alertsData = null;
  // Meetings and provincial endpoints removed - not needed
  
  // Category order is now handled by Zustand store with built-in persistence

  // Handle onboarding completion
  const handleOnboardingComplete = async () => {
    setShowOnboardingModal(false);
    // Refetch onboarding status to update the flag
    await queryClient.invalidateQueries({ queryKey: ["onboarding", "status"] });
    // Also refresh categories in case onboarding selected new ones
    await queryClient.invalidateQueries({ queryKey: ["categories"] });
    toast({
      title: "Onboarding complete!",
      description: "Your preferences have been saved.",
    });
  };


  // Categories are now automatically updated via React Query onSuccess callback and Zustand store
  
  // Calculate category counts from pulse dashboard data
  useEffect(() => {
    if (pulseDashboardData && categories.length > 0) {
      try {
        const counts: Record<string, number> = {};
        
        // Count pulses per category from dashboard data
        categories.forEach((cat) => {
          if (!cat || !cat.id) {
            return;
          }
          
          let categoryCount = 0;
          
          // Count from municipal sources
          if (pulseDashboardData.municipal?.sources) {
            pulseDashboardData.municipal.sources.forEach((source: any) => {
              if (source.categories) {
                source.categories.forEach((catItem: any) => {
                  // Match by category name or display_name
                  if (catItem.name === cat.id || 
                      catItem.name === cat.slug ||
                      catItem.display_name === cat.label ||
                      catItem.id === String(cat.dbId)) {
                    categoryCount += catItem.count || 0;
                  }
                });
              }
            });
          }
          
          // Count from regional sources
          if (pulseDashboardData.regional?.sources) {
            pulseDashboardData.regional.sources.forEach((source: any) => {
              if (source.categories) {
                source.categories.forEach((catItem: any) => {
                  if (catItem.name === cat.id || 
                      catItem.name === cat.slug ||
                      catItem.display_name === cat.label ||
                      catItem.id === String(cat.dbId)) {
                    categoryCount += catItem.count || 0;
                  }
                });
              }
            });
          }
          
          // Count from provincial sources
          if (pulseDashboardData.provincial?.sources) {
            pulseDashboardData.provincial.sources.forEach((source: any) => {
              if (source.categories) {
                source.categories.forEach((catItem: any) => {
                  if (catItem.name === cat.id || 
                      catItem.name === cat.slug ||
                      catItem.display_name === cat.label ||
                      catItem.id === String(cat.dbId)) {
                    categoryCount += catItem.count || 0;
                  }
                });
              }
            });
          }
          
          // Store count by both id (which is the slug) and slug for compatibility
          if (cat.id) {
            counts[cat.id] = categoryCount;
          }
          if (cat.slug && cat.slug !== cat.id) {
            counts[cat.slug] = categoryCount;
          }
        });
        
        console.log('[Dashboard] Updating category counts from pulse data:', counts);
        
        // Update category counts in Zustand store
        updateCategoryCounts(counts);
      } catch (error) {
        console.error('[Dashboard] Error calculating category counts:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulseDashboardData, categories.length]);



  const handleAddCategory = async (name: string, description: string) => {
    try {
      // Use the mutation to create category - this handles optimistic updates and caching
      const savedCategory = await createCategoryMutation.mutateAsync({
        display_name: name,
        description: description
      });
      
      console.log('Topic created:', savedCategory);
      
      // The mutation hook already handles:
      // - Adding to Zustand store
      // - Invalidating React Query cache
      // - Optimistic updates
      
      // Show success message
      console.log('Custom topic saved and UI updated');
      return savedCategory; // Return for success handling in dialog
    } catch (error: any) {
      console.error('Failed to save custom topic:', error);
      // Extract the actual error message from API response
      let errorMessage = 'Failed to save custom topic. Please try again.';
      if (error?.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to save custom topic. Please try again.';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      alert(errorMessage);
      throw error; // Re-throw for error handling in dialog
    }
  };

  const handleRemoveCategory = async (id: string) => {
    try {
      const categoryToRemove = categories.find(c => c.id === id);
      
      if (!categoryToRemove) {
        console.error('Topic not found:', id);
        return;
      }
      
      // Use the stored database ID for the API call
      const dbId = categoryToRemove.dbId;
      
      if (!dbId) {
        console.error('No database ID found for category:', categoryToRemove);
        return;
      }
      
      console.log(`Deleting category with database ID: ${dbId}`);
      
      // Use the mutation to delete category - this handles optimistic updates and caching
      await deleteCategoryMutation.mutateAsync(dbId);
      
      // The mutation hook already handles:
      // - Removing from Zustand store
      // - Invalidating React Query cache
      // - Optimistic updates
      
      console.log('Topic removed successfully');
    } catch (error: any) {
      console.error('Failed to remove topic:', error);
      let errorMessage = 'Failed to remove topic. Please try again.';
      if (error?.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : errorMessage;
      }
      alert(errorMessage);
    }
  };

  // Format stats data for the 4 Business Dashboard cards
  const stats = [
    { 
      label: "New Pulses", 
      value: String(dashboardStats?.new_pulses_today || 0),
      sub: `${dashboardStats?.new_pulses_top3_match || 0} in Top 3 priorities`,
      purpose: dashboardStats?.new_pulses_purpose || "How much new intelligence did Cityscrape add to my workspace today, and how much of it matches what I care about most?"
    },
    { 
      label: "Priority Items (Next 60 Days)", 
      value: String(dashboardStats?.priority_items_count || 0),
      sub: dashboardStats?.priority_next_date 
        ? `Next key date: ${dashboardStats.priority_next_date}` 
        : "No upcoming dates",
      purpose: dashboardStats?.priority_items_purpose || "What priority-matching items have upcoming dates soon, and what is the next key date I should be aware of?"
    },
    { 
      label: "High-Impact Items (Next 60 Days)", 
      value: String(dashboardStats?.high_impact_items_count || 0),
      sub: dashboardStats?.high_impact_next_date 
        ? `Next high-impact date: ${dashboardStats.high_impact_next_date}` 
        : "No upcoming dates",
      purpose: dashboardStats?.high_impact_purpose || "What items are most likely to affect feasibility, cost, permissions, or timelines soon?"
    },
    { 
      label: "Relevance (This Week)", 
      value: dashboardStats?.relevance_percentage || "—%",
      sub: `Top-3 pulses: ${dashboardStats?.relevance_top3_count || 0}`,
      purpose: dashboardStats?.relevance_purpose || "Is the feed aligned to what my workspace cares about, week to week?"
    },
  ];

  // Format pulse items from insights data
  const extractDate = (dateStr: string | Date) => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
      if (isNaN(date.getTime())) return 'Unknown date';
      // Extract just the date part (YYYY-MM-DD) from datetime string
      return date.toISOString().split('T')[0];
    } catch {
      return 'Unknown date';
    }
  };

  const formatDateReadable = (dateStr: string | Date) => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
      if (isNaN(date.getTime())) return 'Unknown date';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Unknown date';
    }
  };

  // No longer creating pulseItems from insights - PulseCenter handles this
  // For URL parameter handling, we'll fetch the specific pulse when needed

  // Handle opening pulse modal from URL query param (e.g., from notification click)
  const lastProcessedInsightRef = React.useRef<string | null>(null);
  
  // Fetch pulse detail when URL has insight parameter
  const insightIdFromUrl = searchParams.get('insight');
  const { data: pulseDetailFromUrl } = useQuery({
    queryKey: ["pulses", "detail", "url", insightIdFromUrl],
    queryFn: async () => {
      if (!insightIdFromUrl) return null;
      try {
        // Try to fetch as municipal first (most common)
        const detail = await apiClient.pulses.getDetail(parseInt(insightIdFromUrl), 'municipal');
        return detail;
      } catch (error) {
        // If municipal fails, try regional or provincial
        try {
          return await apiClient.pulses.getDetail(parseInt(insightIdFromUrl), 'regional');
        } catch {
          try {
            return await apiClient.pulses.getDetail(parseInt(insightIdFromUrl), 'provincial');
          } catch {
            return null;
          }
        }
      }
    },
    enabled: !!insightIdFromUrl && lastProcessedInsightRef.current !== insightIdFromUrl,
  });
  
  useEffect(() => {
    if (insightIdFromUrl && pulseDetailFromUrl && lastProcessedInsightRef.current !== insightIdFromUrl) {
      lastProcessedInsightRef.current = insightIdFromUrl;
      // Convert pulse detail to legacy format for modal
      const legacyItem = {
        id: String(pulseDetailFromUrl.id),
        insightId: pulseDetailFromUrl.insight_id,
        title: pulseDetailFromUrl.title,
        source: pulseDetailFromUrl.source,
        sourceType: pulseDetailFromUrl.source_type,
        category: pulseDetailFromUrl.category,
        date: pulseDetailFromUrl.date,
        isPinned: pulseDetailFromUrl.is_pinned,
        whatChanged: pulseDetailFromUrl.what_changed,
        whoWhereAffects: pulseDetailFromUrl.who_where_affects || "",
        impactOnYou: pulseDetailFromUrl.impact_on_you,
        suggestedAction: pulseDetailFromUrl.suggested_action,
        evidenceUrl: pulseDetailFromUrl.evidence_url,
        status: pulseDetailFromUrl.status || "pending",
        confidence: pulseDetailFromUrl.confidence || 3,
        isRelevant: pulseDetailFromUrl.is_relevant,
        tldrShort: pulseDetailFromUrl.tldr_short,
        whatChangedBullets: pulseDetailFromUrl.what_changed_bullets,
        impact: pulseDetailFromUrl.impact,
        timeSensitivity: pulseDetailFromUrl.time_sensitivity,
        actionBullets: pulseDetailFromUrl.action_bullets,
        itemNumber: pulseDetailFromUrl.item_number,
        publishedDate: pulseDetailFromUrl.published_date,
        meetingDate: pulseDetailFromUrl.meeting_date,
        timeAnchorType: pulseDetailFromUrl.time_anchor_type,
        timeAnchorDate: pulseDetailFromUrl.time_anchor_date,
        timeAnchorLabel: pulseDetailFromUrl.time_anchor_label,
        decisionStatus: pulseDetailFromUrl.decision_status,
      };
      setSelectedPulseItem(legacyItem);
      // Clear the query param from URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [insightIdFromUrl, pulseDetailFromUrl]);
  
  // Reset ref when modal closes so same notification can be clicked again
  useEffect(() => {
    if (!selectedPulseItem) {
      lastProcessedInsightRef.current = null;
    }
  }, [selectedPulseItem]);

  // Alerts removed - using pulses instead
  const alerts: any[] = [];

  const isLoading = categoriesLoading;

  // Dismiss pulse mutation
  const dismissInsightMutation = useMutation({
    mutationFn: ({ insightId, reason }: { insightId: number, reason: 'finished' | 'irrelevant' }) => 
      apiClient.pulses.dismiss(insightId), // Use pulses endpoint instead of insights
    onSuccess: () => {
      // Invalidate pulse queries to refresh the dashboard
      queryClient.invalidateQueries({ queryKey: ["pulses"] });
      toast({
        title: "Pulse dismissed",
        description: "The pulse has been dismissed and removed from your view",
      });
      setDismissItem(null);
      setSelectedPulseItem(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error dismissing insight",
        description: error?.message || "Failed to dismiss insight. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Alerts removed - using pulses instead
  const dismissAlertMutation = null;

  const handleDismiss = (reason: 'finished' | 'irrelevant') => {
    if (!dismissItem) return;
    
    if (dismissItem.type === 'insight') {
      dismissInsightMutation.mutate({ insightId: dismissItem.id, reason });
    } else if (dismissItem.type === 'alert') {
      dismissAlertMutation.mutate({ alertId: dismissItem.id, reason });
    }
  };

  return (
    <TooltipProvider>
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="flex-1 flex flex-col overflow-hidden p-8 max-w-[1800px] mx-auto w-full">
          {categoriesError && (
            <div className="mb-4 p-4 rounded-lg bg-amber-500/20 border border-amber-500/50 text-amber-900 dark:text-amber-100 text-sm space-y-2">
              <p><strong>API connection issue:</strong> {categoriesError}</p>
              <p><strong>Current API base (baked in at build):</strong> <code className="bg-black/20 px-1 rounded break-all">{getApiBaseUrl() || '(empty – requests go to same origin)'}</code></p>
              <p className="text-xs opacity-90">If that is your Vercel URL or empty, set <code className="bg-black/20 px-1 rounded">VITE_API_URL</code> in Vercel to your Railway API URL, then redeploy with <strong>Build Cache disabled</strong> so the new value is used.</p>
            </div>
          )}
          {/* Header with Municipality Filter and Needs Review toggle */}
          <div className="flex items-center justify-between mb-6 flex-shrink-0 flex-wrap gap-3">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Business Dashboard</h1>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-600 dark:text-neutral-400 whitespace-nowrap">Pulses found:</span>
                <Select value={pulsesFoundPeriod} onValueChange={setPulsesFoundPeriod}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Time range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="last_7_days">Last 7 days</SelectItem>
                    <SelectItem value="last_30_days">Last 30 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div
                className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 cursor-pointer select-none"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setShowNeedsReview((prev) => {
                    const next = !prev;
                    console.log("[ShowNeedsReview] toggled:", prev, "->", next);
                    return next;
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowNeedsReview((prev) => !prev);
                  }
                }}
              >
                <span onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={showNeedsReview}
                    onCheckedChange={(checked) => {
                      console.log("[ShowNeedsReview] switch:", checked);
                      setShowNeedsReview(checked);
                    }}
                  />
                </span>
                <span>Needs Review {showNeedsReview ? "(Showing)" : "(Hidden)"}</span>
              </div>
              <MunicipalityFilter />
            </div>
          </div>
          
          {/* Stats Grid - 4 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-shrink-0 mb-8">
            {stats.map((stat, i) => (
                <div 
                  key={stat.label}
                  className="bg-[#1A1C1E] p-4 rounded-lg border border-white/10 shadow-sm flex flex-col justify-between h-28 relative group hover:border-[#BADA55]/50 transition-colors"
                >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">{stat.label}</div>
                    <motion.div 
                      key={`${stat.label}-value-${stat.value}`}
                      initial={false}
                      animate={{ opacity: 1 }}
                      className="text-3xl font-bold tracking-tight text-white"
                    >
                      {stat.value}
                    </motion.div>
                  </div>
                  {/* Info icon with tooltip */}
                  <div className="relative group/info">
                    <Info 
                      className="w-4 h-4 text-primary cursor-help shrink-0 ml-2" 
                    />
                    <div 
                      className="absolute right-0 top-full mt-2 w-80 p-3 text-xs rounded-[20px] opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-200 z-[100] pointer-events-none overflow-hidden"
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(5px)',
                        WebkitBackdropFilter: 'blur(5px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.1), inset 0 0 0px 0px rgba(255, 255, 255, 0)'
                      }}
                    >
                      {/* Top gradient line */}
                      <div 
                        className="absolute top-0 left-0 right-0 h-px"
                        style={{
                          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)'
                        }}
                      ></div>
                      {/* Left gradient line */}
                      <div 
                        className="absolute top-0 left-0 w-px h-full"
                        style={{
                          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3))'
                        }}
                      ></div>
                      <div className="text-primary leading-relaxed relative z-10 font-semibold">{stat.purpose}</div>
                      {/* Arrow pointing up to icon */}
                      <div 
                        className="absolute bottom-full right-6 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-transparent"
                        style={{ borderBottomColor: 'rgba(255, 255, 255, 0.05)' }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center mt-1">
                  <motion.div 
                    key={`${stat.label}-sub-${stat.sub}`}
                    initial={false}
                    animate={{ opacity: 1 }}
                    className="text-xs text-neutral-400 truncate"
                  >
                    {stat.sub}
                  </motion.div>
                </div>
              </div>
            ))}
          </div>

          {/* Pulse Center - Three Column Layout with Pinned Pulses */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <PulseCenter 
              onOpenCategoryModal={() => setIsManageCategoriesOpen(true)} 
              pulseType="business"
              municipalityId={selectedMunicipalityId}
              showNeedsReview={showNeedsReview}
              foundDatePeriod={pulsesFoundPeriod && pulsesFoundPeriod !== "all" ? pulsesFoundPeriod : null}
            />
          </div>

        </div>
      </main>
      
      {/* Detailed Item Modal */}
      <Dialog open={!!selectedPulseItem} onOpenChange={(open) => !open && setSelectedPulseItem(null)}>
        <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden border-none shadow-2xl">
           {selectedPulseItem && (
             <>
               <DialogHeader className="sr-only">
                 <DialogTitle>{selectedPulseItem.title || 'Industry Pulse Item Details'}</DialogTitle>
                 <DialogDescription>View detailed information about this industry pulse item</DialogDescription>
               </DialogHeader>
               {/* Header */}
               <div className="p-6 bg-white dark:bg-neutral-900 border-b">
                 <div className="flex justify-between items-start gap-4 mb-4">
                    <h2 className="text-xl font-bold leading-snug text-neutral-900 dark:text-white">
                      {selectedPulseItem.title}
                    </h2>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-[10px] font-semibold uppercase tracking-wider shrink-0",
                        selectedPulseItem.type === 'PROVINCIAL' && "border-blue-500 text-blue-700 bg-blue-50",
                        selectedPulseItem.type === 'REGIONAL' && "border-purple-500 text-purple-700 bg-purple-50",
                        selectedPulseItem.type === 'MUNICIPAL' && "border-neutral-400 text-neutral-600"
                      )}
                    >
                      {selectedPulseItem.type}
                    </Badge>
          </div>

                 <div className="flex items-center gap-3 flex-wrap">
                   <span className="text-sm text-neutral-500 font-medium">
                     {selectedPulseItem.rawInsight?.public_event_title || selectedPulseItem.authority}
                   </span>
                   {selectedPulseItem.municipalityName && (
                     <>
                       <span className="text-neutral-300 text-sm">•</span>
                       <span className="text-sm text-neutral-500">
                         {selectedPulseItem.municipalityName}
                       </span>
                     </>
                   )}
                  </div>
                </div>

               {/* Body with Tabs */}
               <div className="p-6 bg-[#FAFAFA] dark:bg-neutral-950 max-h-[70vh] overflow-y-auto">
                 <Tabs defaultValue="timeline" className="w-full">
                   <TabsList className="grid w-full grid-cols-2 mb-4">
                     <TabsTrigger value="timeline" className="text-xs">Timeline & Decisions</TabsTrigger>
                     <TabsTrigger value="overview" className="text-xs">Summary & Details</TabsTrigger>
                   </TabsList>

                   {/* Summary & Details Tab */}
                   <TabsContent value="overview" className="mt-0 space-y-4">
                     {/* Metadata Grid */}
                     <div className="grid grid-cols-2 gap-3">
                       <div className="bg-white dark:bg-neutral-900 p-3 rounded-lg border border-neutral-200 dark:border-neutral-800">
                         <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">
                           Meeting Date
                         </div>
                         <div className="font-medium text-sm flex items-center gap-2">
                           <Calendar className="h-3.5 w-3.5 text-neutral-400" />
                           {formatDateReadable(selectedPulseItem.rawInsight?.meeting_date || selectedPulseItem.rawInsight?.publish_date || selectedPulseItem.date)}
                         </div>
                       </div>
                       {selectedPulseItem.sourceChannel !== 'provincial' && (
                         <div className="bg-white dark:bg-neutral-900 p-3 rounded-lg border border-neutral-200 dark:border-neutral-800">
                           <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1">
                             Item #
                           </div>
                           <div className="font-mono text-sm">
                             {selectedPulseItem.itemNo || selectedPulseItem.rawInsight?.item_number || "N/A"}
                           </div>
                         </div>
                       )}
                     </div>
                     
                     {/* Category Box */}
                     {(selectedPulseItem.allCategories?.length > 0 || selectedPulseItem.rawInsight?.category) && (
                       <div className="bg-white dark:bg-neutral-900 p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 w-full">
                         <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-2">
                           {selectedPulseItem.allCategories?.length > 1 ? 'Topics' : 'Topic'}
                         </div>
                         <div className="flex flex-wrap gap-2">
                           {selectedPulseItem.allCategories?.length > 0 ? (
                             selectedPulseItem.allCategories.map((cat: any, i: number) => (
                               <Badge key={i} variant="secondary" className="bg-neutral-100 hover:bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 font-normal border border-neutral-200 dark:border-neutral-700">
                                 {cat.display_name}
                               </Badge>
                             ))
                           ) : (
                             <span className="text-sm text-neutral-700 dark:text-neutral-300">
                               {selectedPulseItem.rawInsight?.category_display_name || selectedPulseItem.rawInsight?.category}
                             </span>
                           )}
                         </div>
                       </div>
                     )}

                     {/* Meeting Summary */}
                     {selectedPulseItem.aiSummary && (
                       <div className="space-y-2">
                         <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">Meeting Summary</h4>
                         <p className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed">
                           {selectedPulseItem.aiSummary}
                         </p>
                       </div>
                     )}
                   </TabsContent>

                   {/* Timeline & Decisions Tab */}
                   <TabsContent value="timeline" className="mt-0 space-y-6">
                     <div className="relative pl-4 space-y-6 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gradient-to-b before:from-neutral-200 before:via-neutral-200 before:to-transparent dark:before:from-neutral-800 dark:before:via-neutral-800">
                       
                       {/* Pre-Meeting Node */}
                       <div className="relative">
                         <div className={cn(
                           "absolute -left-[21px] top-0.5 h-3.5 w-3.5 rounded-full border-2 z-10",
                           !selectedPulseItem.rawInsight?.outcome_summary && selectedPulseItem.rawInsight?.status === 'pending'
                             ? "border-blue-500 bg-blue-500 shadow-[0_0_0_4px_rgba(59,130,246,0.15)]" 
                             : "border-neutral-300 bg-white dark:border-neutral-700 dark:bg-neutral-900"
                         )} />
                         <div className="space-y-2">
                           <div className="flex items-center gap-2">
                             <span className={cn(
                               "text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md",
                               !selectedPulseItem.rawInsight?.outcome_summary && selectedPulseItem.rawInsight?.status === 'pending'
                                 ? "text-blue-700 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400"
                                 : "text-neutral-500 bg-neutral-100 dark:bg-neutral-800"
                             )}>
                               Pre-Meeting
                             </span>
                             <span className="text-xs text-neutral-400">Agenda Published</span>
                           </div>
                           <div className={cn(
                             "border rounded-lg p-4 shadow-sm",
                             !selectedPulseItem.rawInsight?.outcome_summary && selectedPulseItem.rawInsight?.status === 'pending'
                               ? "bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/50"
                               : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800"
                           )}>
                             <h4 className="font-semibold text-sm text-neutral-900 dark:text-white mb-2 flex items-center gap-2">
                               <FileText className={cn("h-4 w-4", !selectedPulseItem.rawInsight?.outcome_summary && selectedPulseItem.rawInsight?.status === 'pending' ? "text-blue-500" : "text-neutral-400")} />
                               What's On The Agenda
                             </h4>
                             <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                               {selectedPulseItem.rawInsight?.summary || selectedPulseItem.description || "No agenda details available."}
                             </p>
                           </div>
                         </div>
                       </div>

                       {/* After Meeting Node */}
                       {selectedPulseItem.rawInsight?.outcome_summary || (selectedPulseItem.rawInsight?.status && selectedPulseItem.rawInsight?.status !== 'pending') ? (
                         <div className="relative">
                           <div className="absolute -left-[21px] top-0.5 h-3.5 w-3.5 rounded-full border-2 border-green-500 bg-green-500 shadow-[0_0_0_4px_rgba(34,197,94,0.15)] z-10" />
                           <div className="space-y-2">
                             <div className="flex items-center gap-2">
                               <span className="text-xs font-bold uppercase tracking-wider text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/30 px-2 py-0.5 rounded-md">
                                 After Meeting
                               </span>
                               <span className="text-xs text-neutral-400">Minutes Released</span>
                             </div>
                             <div className="bg-green-50/50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/50 rounded-lg p-4 shadow-sm">
                               <h4 className="font-semibold text-sm text-neutral-900 dark:text-white mb-2 flex items-center gap-2">
                                 <Gavel className="h-4 w-4 text-green-600 dark:text-green-400" />
                                 Council Decision
                               </h4>
                               <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                                 {selectedPulseItem.rawInsight?.outcome_summary || `Item was ${selectedPulseItem.rawInsight?.status}.`}
                               </p>
                               
                               {/* Vote & Resolution Details */}
                               {(selectedPulseItem.rawInsight?.vote_result || selectedPulseItem.rawInsight?.resolution_number) && (
                                 <div className="mt-3 pt-3 border-t border-green-200/50 dark:border-green-800/30">
                                   <div className="flex flex-wrap gap-3">
                                     {selectedPulseItem.rawInsight?.vote_result && (
                                       <div className="text-xs">
                                         <span className="font-medium text-neutral-500">Vote:</span>{' '}
                                         <span className="font-semibold text-neutral-700 dark:text-neutral-300">{selectedPulseItem.rawInsight.vote_result}</span>
                                       </div>
                                     )}
                                     {selectedPulseItem.rawInsight?.resolution_number && (
                                       <div className="text-xs">
                                         <span className="font-medium text-neutral-500">Resolution:</span>{' '}
                                         <span className="font-mono font-semibold text-neutral-700 dark:text-neutral-300">{selectedPulseItem.rawInsight.resolution_number}</span>
                                       </div>
                                     )}
                                   </div>
                                 </div>
                               )}
                             </div>
                           </div>
                         </div>
                       ) : (
                         /* Upcoming Meeting Placeholder */
                         <div className="relative">
                           <div className="absolute -left-[21px] top-0.5 h-3.5 w-3.5 rounded-full border-2 z-10" style={{ borderColor: '#BADA55', backgroundColor: '#BADA5533' }} />
                           <div className="space-y-2">
                             <div className="flex items-center gap-2">
                               <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md" style={{ color: '#6B8E23', backgroundColor: '#BADA5522' }}>
                                 Upcoming
                               </span>
                               <span className="text-xs text-neutral-400">
                                 <Clock className="h-3 w-3 inline mr-1" />
                                 Awaiting minutes
                               </span>
                             </div>
                             <div className="p-4 border border-dashed rounded-lg" style={{ borderColor: '#BADA5566', backgroundColor: '#BADA5511' }}>
                               <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">
                                 Meeting outcome pending. Get notified when minutes are published.
                               </p>
                               {subscribedInsights.has(selectedPulseItem.rawInsight?.id) ? (
                                 <button 
                                   className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all hover:opacity-80 cursor-pointer group"
                                   style={{ backgroundColor: '#BADA55', color: '#1a1a1a' }}
                                   onClick={async () => {
                                     const insightId = selectedPulseItem.rawInsight?.id;
                                     try {
                                       await apiClient.delete(`/api/notifications/insights/${insightId}/unsubscribe`);
                                     } catch (error) {
                                       // Ignore errors
                                     }
                                     setSubscribedInsights(prev => {
                                       const next = new Set(prev);
                                       next.delete(insightId);
                                       return next;
                                     });
                                   }}
                                 >
                                   <Check className="h-4 w-4 group-hover:hidden" />
                                   <X className="h-4 w-4 hidden group-hover:block" />
                                   <span className="group-hover:hidden">Subscribed</span>
                                   <span className="hidden group-hover:inline">Unsubscribe</span>
                                 </button>
                               ) : (
                                 <Button
                                   variant="outline"
                                   size="sm"
                                   className="gap-2"
                                   style={{ borderColor: '#BADA55', color: '#6B8E23' }}
                                   onClick={async () => {
                                     const insightId = selectedPulseItem.rawInsight?.id;
                                     try {
                                       await apiClient.post('/api/notifications/insights/subscribe', {
                                         insight_id: insightId
                                       });
                                     } catch (error) {
                                       // Already subscribed - still mark as subscribed
                                     }
                                     setSubscribedInsights(prev => new Set(prev).add(insightId));
                                   }}
                                 >
                                   <Bell className="h-3.5 w-3.5" />
                                   Notify Me
                                 </Button>
                               )}
                             </div>
                           </div>
                         </div>
                       )}
                     </div>
                   </TabsContent>
                 </Tabs>
               </div>

               {/* Footer */}
               <div className="p-4 bg-white dark:bg-neutral-900 border-t flex items-center justify-between gap-3">
                  <Button 
                    variant="ghost" 
                    className="text-neutral-500 hover:text-neutral-700" 
                    onClick={() => {
                      if (selectedPulseItem?.rawInsight?.id) {
                        setDismissItem({
                          id: selectedPulseItem.rawInsight.id,
                          type: 'insight',
                          title: selectedPulseItem.title || 'Insight'
                        });
                      }
                    }}
                  >
                    Dismiss
                  </Button>
                  <div className="flex items-center gap-2">
                    {selectedPulseItem.documentUrl && (
                      <Button 
                        variant="outline" 
                        className="gap-2 bg-white hover:bg-neutral-50"
                        onClick={() => {
                          window.open(selectedPulseItem.documentUrl, '_blank');
                        }}
                      >
                        <ExternalLink className="h-4 w-4" />
                        View Document
                      </Button>
                    )}
                    {selectedPulseItem.documentId && selectedPulseItem.id && (
                      <Button 
                        className="gap-2 bg-primary hover:bg-primary/90 text-white shadow-sm"
                        onClick={() => {
                          setActiveChatInsight(selectedPulseItem);
                          setShowChatWidget(true);
                        }}
                      >
                        <Sparkles className="h-4 w-4" />
                        Ask AI
                      </Button>
                    )}
            </div>
          </div>
             </>
           )}
        </DialogContent>
      </Dialog>

      {/* Manage Topics Dialog */}
      <ManageCategoriesDialog 
        open={isManageCategoriesOpen} 
        onOpenChange={setIsManageCategoriesOpen}
        categories={categories}
        onAdd={handleAddCategory}
        onRemove={handleRemoveCategory}
        onReorder={(newOrder) => {
          // Use Zustand store to reorder categories
          // The store handles persistence automatically
          reorderCategories(newOrder.map(cat => cat.id));
        }}
        onUpdate={(id, updates) => {
          // Update category in the store
          updateCategory(id, updates);
        }}
        refetchCategories={refetchCategories}
        createCategoryMutation={createCategoryMutation}
        updateCategoryMutation={updateCategoryMutation}
        filterOptions={filterOptions}
      />

      {/* Alerts removed - using pulses instead */}

      {/* Filter Insights Modal */}
      <FilterInsightsModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        selectedLevels={selectedLevels}
        selectedMunicipalities={selectedMunicipalities}
        selectedProvStreams={selectedProvStreams}
        selectedRegions={selectedRegions}
        municipalOptions={filterOptions?.municipalities || []}
        provincialStreamOptions={filterOptions?.provincialStreams || []}
        regionalOptions={filterOptions?.regions || []}
        sortBy={sortBy}
        onToggleLevel={(level) => {
          setSelectedLevels(prev => 
            prev.includes(level) 
              ? prev.filter(l => l !== level)
              : [...prev, level]
          );
        }}
        onToggleMunicipality={(value) => {
          setSelectedMunicipalities(prev =>
            prev.includes(value)
              ? prev.filter(m => m !== value)
              : [...prev, value]
          );
        }}
        onToggleProvincialStream={(value) => {
          setSelectedProvStreams(prev =>
            prev.includes(value)
              ? prev.filter(p => p !== value)
              : [...prev, value]
          );
        }}
        onToggleRegion={(value) => {
          setSelectedRegions(prev =>
            prev.includes(value)
              ? prev.filter(r => r !== value)
              : [...prev, value]
          );
        }}
        onSortChange={(newSortBy) => {
          setSortBy(newSortBy);
        }}
        onResetFilters={() => {
          setSelectedLevels([]);
          setSelectedMunicipalities([]);
          setSelectedProvStreams([]);
          setSelectedRegions([]);
          setSortBy('event_date'); // Reset to default
        }}
        hasActiveFilters={
          selectedLevels.length > 0 ||
          selectedMunicipalities.length > 0 ||
          selectedProvStreams.length > 0 ||
          selectedRegions.length > 0 ||
          sortBy !== 'event_date'
        }
      />


      {/* Dismiss Modal */}
      {dismissItem && (
        <DismissModal
          itemId={dismissItem.id}
          itemTitle={dismissItem.title}
          itemType={dismissItem.type}
          onClose={() => setDismissItem(null)}
          onDismiss={handleDismiss}
        />
      )}

      {/* ChatWidget for AI Conversations */}
      {showChatWidget && activeChatInsight && (
        <ChatWidget
          insightId={activeChatInsight.id}
          documentTitle={activeChatInsight.title || 'Insight Document'}
          propertyName={activeChatInsight.municipalityName || 'Your Municipality'}
          onClose={() => {
            setShowChatWidget(false);
            setActiveChatInsight(null);
          }}
        />
      )}

      {/* Onboarding Modal */}
      <OnboardingModal
        open={showOnboardingModal}
        onComplete={handleOnboardingComplete}
      />

        </div>
    </TooltipProvider>
  );
}

function ManageCategoriesDialog({ 
  open, 
  onOpenChange, 
  categories, 
  onAdd, 
  onRemove,
  onReorder,
  onUpdate,
  refetchCategories,
  createCategoryMutation,
  updateCategoryMutation,
  filterOptions
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void; 
  categories: any[]; 
  onAdd: (name: string, description: string) => void; 
  onRemove: (id: string) => void;
  onReorder: (newOrder: any[]) => void;
  onUpdate: (id: string, updates: any) => void;
  refetchCategories: () => void;
  createCategoryMutation: any;
  updateCategoryMutation: any;
  filterOptions: { municipalities: string[]; regions: string[]; provincialStreams: string[] };
}) {
  const queryClient = useQueryClient();
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedProvincial, setSelectedProvincial] = useState<string[]>([]);
  const [monitorAllSources, setMonitorAllSources] = useState(false);
  const [showSourceRequiredAlert, setShowSourceRequiredAlert] = useState(false);
  const [expandedCommittees, setExpandedCommittees] = useState<string[]>([]);
  const [selectedCommittees, setSelectedCommittees] = useState<string[]>([]);
  const [municipalitiesDropdownOpen, setMunicipalitiesDropdownOpen] = useState(false);
  const [regionsDropdownOpen, setRegionsDropdownOpen] = useState(false);
  const [provincialDropdownOpen, setProvincialDropdownOpen] = useState(false);
  
  // Fetch ALL default categories with their follow status (cache and reuse, only refetch when needed)
  const { data: allDefaultsData, refetch: refetchAllDefaults } = useQuery({
    queryKey: ['all-default-categories'],
    queryFn: async () => {
      const response = await apiClient.topics.getAllDefaults();
      return response;
    },
    staleTime: Infinity, // Never consider data stale - use cached data unless explicitly refetched
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnMount: false, // Don't refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on reconnect
  });
  
  // Fetch municipalities, regions, and provincial sources from API
  // Filter by source_type for each dropdown:
  // - Municipal dropdown: source_type = 'municipal'
  // - Regional dropdown: source_type = 'regional'
  // - Provincial dropdown: source_type = 'provincial'
  // Properties endpoints removed - using projects instead
  // Filter options now come from pulseDashboardData (filterOptions)
  const municipalitiesList = (filterOptions?.municipalities || []).map((name: string) => ({
    id: name,
    label: name,
    name: name
  }));
  
  const regionsList = (filterOptions?.regions || []).map((name: string) => ({
    id: name,
    label: name,
    name: name
  }));
  
  const provincialList = (filterOptions?.provincialStreams || []).map((name: string) => ({
    id: name,
    label: name,
    name: name
  }));
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const { toast } = useToast();
  // Track pending changes to default categories (slug -> new is_followed state)
  const [pendingCategoryChanges, setPendingCategoryChanges] = useState<Map<string, boolean>>(new Map());
  // Track pending changes to custom categories (id -> {label, description, etc})
  const [pendingCustomCategoryChanges, setPendingCustomCategoryChanges] = useState<Map<string, any>>(new Map());
  const [isSavingCategories, setIsSavingCategories] = useState(false);
  
  // Track temporary/new categories that haven't been saved yet
  const [temporaryCategories, setTemporaryCategories] = useState<any[]>([]);
  
  // Track original state when modal opens
  const [originalDefaultStates, setOriginalDefaultStates] = useState<Map<string, boolean>>(new Map());
  const [originalCustomCategories, setOriginalCustomCategories] = useState<Set<string>>(new Set());
  
  // Track category notification preferences
  const [categoryNotifications, setCategoryNotifications] = useState<Map<string, boolean>>(new Map());
  const [togglingNotification, setTogglingNotification] = useState<string | null>(null);

  // Separate categories into default and custom based on isCustom field
  // Default categories: Use all defaults from API (shows all defaults with follow status)
  // Custom categories: Categories with isCustom = TRUE (company-specific, regardless of is_default)
  const defaultCategoriesList = Array.isArray(allDefaultsData) ? allDefaultsData : [];
  const defaultCategories = defaultCategoriesList.length > 0
    ? defaultCategoriesList.map((cat: any) => ({
        id: cat.slug || `cat-${cat.id}`,
        dbId: cat.id,
        slug: cat.slug,
        label: cat.display_name || cat.slug,
        description: cat.description || '',
        is_default: cat.is_default,
        isCustom: false, // These are global defaults
        // Use pending changes if available, otherwise use original state
        is_followed: pendingCategoryChanges.has(cat.slug) 
          ? pendingCategoryChanges.get(cat.slug)! 
          : cat.is_followed,
        is_priority: cat.is_priority || false,
        priority_order: cat.priority_order,
        is_active: cat.is_active,
        count: 0, // Will be updated from categories store if needed
        active: false,
      }))
    : categories.filter((cat: any) => cat.is_default === true && cat.isCustom === false); // Fallback to current categories

  // Count current priorities
  const priorityCount = defaultCategories.filter((cat: any) => cat.is_priority).length;
  
  // Combine existing custom categories with temporary ones
  const existingCustomCategories = categories.filter((cat: any) => cat.isCustom === true);
  const customCategories = [...existingCustomCategories, ...temporaryCategories];
  
  // Capture original state when modal opens and fetch notification preferences
  // Use a ref to track if we've already captured the original states for this modal session
  const hasCapturedOriginalStates = React.useRef(false);
  
  useEffect(() => {
    if (open && Array.isArray(allDefaultsData) && allDefaultsData.length > 0 && !hasCapturedOriginalStates.current) {
      // Store original default category states - ensure we capture the actual boolean values
      // Only capture once when modal first opens, not when allDefaultsData changes
      const originalDefaults = new Map<string, boolean>();
      allDefaultsData.forEach((cat: any) => {
        originalDefaults.set(cat.slug, Boolean(cat.is_followed));
      });
      console.log('[Modal] Captured original states:', Array.from(originalDefaults.entries()).map(([slug, val]) => `${slug}=${val}`).join(', '));
      setOriginalDefaultStates(originalDefaults);
      hasCapturedOriginalStates.current = true;
      
      // Store original custom category IDs
      const originalCustom = new Set();
      customCategories.forEach((cat: any) => {
        originalCustom.add(cat.id);
      });
      setOriginalCustomCategories(originalCustom);
      
      // Fetch notification preferences
      const fetchNotificationPreferences = async () => {
        try {
          const prefs = await apiClient.get('/api/notifications/preferences/categories');
          const prefsMap = new Map();
          prefs.forEach((pref: any) => {
            prefsMap.set(pref.category_slug, pref.notify_enabled);
          });
          setCategoryNotifications(prefsMap);
        } catch (error) {
          console.error('Error fetching notification preferences:', error);
        }
      };
      fetchNotificationPreferences();
    }
    
    // Reset the ref when modal closes so we capture original states again next time
    if (!open) {
      hasCapturedOriginalStates.current = false;
    }
  }, [open, allDefaultsData, customCategories]);
  
  // Check if there are any pending changes (including temporary categories)
  const hasChanges = pendingCategoryChanges.size > 0 || pendingCustomCategoryChanges.size > 0 || temporaryCategories.length > 0;
  
  // Toggle category notification
  const toggleCategoryNotification = async (categorySlug: string, currentEnabled: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    setTogglingNotification(categorySlug);
    
    try {
      await apiClient.post('/api/notifications/preferences/categories', {
        category_slug: categorySlug,
        notify_enabled: !currentEnabled
      });
      
      // Update local state
      setCategoryNotifications(prev => {
        const updated = new Map(prev);
        updated.set(categorySlug, !currentEnabled);
        return updated;
      });
      
      toast({
        title: !currentEnabled ? "Notifications enabled" : "Notifications disabled",
        description: `You'll receive notifications for new insights in this category`,
      });
    } catch (error) {
      console.error('Error toggling notification:', error);
      toast({
        title: "Error",
        description: "Failed to update notification preference",
        variant: "destructive"
      });
    } finally {
      setTogglingNotification(null);
    }
  };

  const toggleDefaultCategory = (categoryId: string) => {
    // Toggle follow/unfollow for a default category (only update local state, don't save yet)
    const category = defaultCategories.find((c: any) => c.id === categoryId);
    
    if (!category || !category.slug) {
      toast({
        title: "Error",
        description: "Topic not found. Please refresh and try again.",
        variant: "destructive"
      });
      return;
    }
    
    // Get the original state from when the modal opened (before any pending changes)
    // ALWAYS use originalDefaultStates - never fall back to category.is_followed or allDefaultsData because they get mutated
    if (!originalDefaultStates.has(category.slug)) {
      console.error(`[ToggleCategory] ❌ Original state not found in originalDefaultStates for ${category.slug}! Available keys:`, Array.from(originalDefaultStates.keys()));
    }
    const originalState = originalDefaultStates.has(category.slug) 
      ? Boolean(originalDefaultStates.get(category.slug))
      : false; // Default to false if not found (shouldn't happen)
    
    // Get current state (from pending changes or original)
    const currentState = pendingCategoryChanges.has(category.slug)
      ? Boolean(pendingCategoryChanges.get(category.slug))
      : originalState;
    const newFollowedState = !currentState;
    
    console.log(`[ToggleCategory] ${category.slug}: original=${originalState}, current=${currentState}, new=${newFollowedState}, willMatch=${newFollowedState === originalState}`);
    
    // Update pending changes
    setPendingCategoryChanges(prev => {
      const updated = new Map(prev);
      
      // If the new state matches the original state, remove it from pending changes
      // Use strict boolean comparison
      if (newFollowedState === originalState) {
        updated.delete(category.slug);
        console.log(`[ToggleCategory] ✅ Reverted ${category.slug} to original state (${originalState}), removed from pending changes. Map size: ${updated.size}`);
      } else {
        // Otherwise, add/update the pending change
        updated.set(category.slug, newFollowedState);
        console.log(`[ToggleCategory] ⚠️ Changed ${category.slug} to ${newFollowedState} (original was ${originalState}), added to pending changes. Map size: ${updated.size}`);
      }
      
      return updated;
    });
    
    // Update local display state for immediate UI feedback
    if (Array.isArray(allDefaultsData) && allDefaultsData.length > 0) {
      const updated = allDefaultsData.map((cat: any) => 
        cat.slug === category.slug ? { ...cat, is_followed: newFollowedState } : cat
      );
      queryClient.setQueryData(['all-default-categories'], updated);
    }
  };
  
  const [togglingPriority, setTogglingPriority] = useState<string | null>(null);
  
  const toggleCategoryPriority = async (categoryDbId: number, categorySlug: string, currentPriority: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Check if we're at max and trying to add another
    if (!currentPriority && priorityCount >= 3) {
      toast({
        title: "Maximum priorities reached",
        description: "You can only set 3 priorities. Remove one first.",
        variant: "destructive"
      });
      return;
    }
    
    setTogglingPriority(categorySlug);
    
    try {
      if (currentPriority) {
        await apiClient.topics.unsetPriority(categoryDbId);
      } else {
        await apiClient.topics.setPriority(categoryDbId);
      }
      
      // Refetch categories to update priority state
      await refetchAllDefaults();
      
      toast({
        title: currentPriority ? "Priority removed" : "Priority set",
        description: currentPriority 
          ? "This category is no longer a priority" 
          : "This category is now one of your Top 3 priorities"
      });
    } catch (error: any) {
      console.error('Error toggling priority:', error);
      toast({
        title: "Error",
        description: error?.response?.data?.detail || "Failed to update priority",
        variant: "destructive"
      });
    } finally {
      setTogglingPriority(null);
    }
  };

  const applyCategoryChanges = async () => {
    const hasDefaultChanges = pendingCategoryChanges.size > 0;
    const hasCustomChanges = pendingCustomCategoryChanges.size > 0;
    const hasNewCategories = temporaryCategories.length > 0;
    
    console.log('[ApplyChanges] Checking for changes:', {
      pendingCategoryChanges: pendingCategoryChanges.size,
      pendingCustomCategoryChanges: pendingCustomCategoryChanges.size,
      temporaryCategories: temporaryCategories.length,
      hasDefaultChanges,
      hasCustomChanges,
      hasNewCategories
    });
    
    if (!hasDefaultChanges && !hasCustomChanges && !hasNewCategories) {
      // No changes, just close
      console.log('[ApplyChanges] No changes detected, closing modal without saving');
      onOpenChange(false);
      return;
    }
    
    const totalChanges = pendingCategoryChanges.size + pendingCustomCategoryChanges.size + temporaryCategories.length;
    setIsSavingCategories(true);
    
    try {
      const promises: Promise<any>[] = [];
      
      // Apply pending default category changes
      if (hasDefaultChanges) {
        Array.from(pendingCategoryChanges.entries()).forEach(([slug, shouldFollow]) => {
          if (shouldFollow) {
            promises.push(apiClient.post(`/api/custom-categories/${slug}/follow`, {}));
          } else {
            promises.push(apiClient.post(`/api/custom-categories/${slug}/unfollow`, {}));
          }
        });
      }
      
      // Create new temporary categories
      if (hasNewCategories) {
        for (const tempCategory of temporaryCategories) {
          const updates = pendingCustomCategoryChanges.get(tempCategory.id) || {};
          const hasMonitorAll = updates.monitor_all === true;
          const hasMonitoringSources = updates.monitoring_sources && updates.monitoring_sources.length > 0;
          
          console.log('[DEBUG] Creating temp category:', {
            tempCategoryId: tempCategory.id,
            updates,
            hasMonitorAll,
            hasMonitoringSources,
            monitoring_sources: updates.monitoring_sources
          });
          
          // Validate: must have monitor_all OR at least one source
          if (!hasMonitorAll && !hasMonitoringSources) {
            toast({
              title: "Source Selection Required",
              description: `Please select at least one source to monitor for "${updates.label || tempCategory.label}", or enable "Monitor All Sources".`,
              variant: "destructive",
            });
            setIsSavingCategories(false);
            return;
          }
          
          const createData: any = {
            display_name: updates.label !== undefined ? updates.label : tempCategory.label,
            description: updates.description !== undefined ? updates.description : tempCategory.description,
          };
          
          if (hasMonitorAll) {
            createData.monitor_all = true;
          } else if (hasMonitoringSources) {
            createData.monitoring_sources = updates.monitoring_sources;
          }
          
          console.log('[DEBUG] Sending createData to API:', createData);
          
          promises.push(createCategoryMutation.mutateAsync(createData));
        }
      }
      
      // Apply pending custom category changes (updates to existing categories)
      if (hasCustomChanges) {
        Array.from(pendingCustomCategoryChanges.entries()).forEach(([id, updates]) => {
          // Skip temporary categories (they're handled above)
          if (id.startsWith('temp-')) {
            return;
          }
          
          const category = categories.find(cat => cat.id === id);
          if (category && category.dbId) {
            // Check if there are actual changes
            const hasLabelChange = updates.label !== undefined && updates.label !== category.label;
            const hasDescriptionChange = updates.description !== undefined && updates.description !== category.description;
            
            // Compare monitoring sources
            const currentSources = category.monitoring_sources || [];
            const newSources = updates.monitoring_sources || [];
            const hasMonitoringSourcesChange = JSON.stringify(currentSources.sort((a: any, b: any) => 
              `${a.source_type}-${a.source_id}`.localeCompare(`${b.source_type}-${b.source_id}`)
            )) !== JSON.stringify(newSources.sort((a: any, b: any) => 
              `${a.source_type}-${a.source_id}`.localeCompare(`${b.source_type}-${b.source_id}`)
            ));
            
            // Only update if there are actual changes
            if (!hasLabelChange && !hasDescriptionChange && !hasMonitoringSourcesChange) {
              return; // Skip this category - no actual changes
            }
            
            const updateData: any = {};
            
            if (hasLabelChange) {
              updateData.display_name = updates.label;
            }
            if (hasDescriptionChange) {
              updateData.description = updates.description;
            }
            if (hasMonitoringSourcesChange) {
              updateData.monitoring_sources = updates.monitoring_sources;
            }
            
            promises.push(
              updateCategoryMutation.mutateAsync({
                id: category.dbId,
                data: updateData
              })
            );
          }
        });
      }
      
      await Promise.all(promises);
      
      // Count actual changes that were saved (not just pending)
      const actualChangesCount = promises.length;
      
      // Clear pending changes and temporary categories
      setPendingCategoryChanges(new Map());
      setPendingCustomCategoryChanges(new Map());
      setTemporaryCategories([]);
      setOriginalDefaultStates(new Map());
      setOriginalCustomCategories(new Set());
      
      // Refetch both defaults and main categories list so changes show immediately
      await Promise.all([
        refetchAllDefaults(),
        refetchCategories()
      ]);
      
      // Also invalidate the followed categories query used by PulseCenter Topics section
      queryClient.invalidateQueries({ queryKey: ["categories", "followed"] });
      
      // Only show toast if there were actual changes saved
      if (actualChangesCount > 0) {
        toast({
          title: "Topics Updated",
          description: `Successfully updated ${actualChangesCount} topic${actualChangesCount === 1 ? '' : 's'}.`,
        });
      }
      
      // Close the dialog after successful save
      onOpenChange(false);
    } catch (error: any) {
      console.error('Failed to save topic changes:', error);
      let errorMessage = "Failed to update topics. Please try again.";
      if (error?.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : errorMessage;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSavingCategories(false);
    }
  };

  const toggleMunicipality = (id: string) => {
    setSelectedMunicipalities(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  const toggleRegion = (id: string) => {
    setSelectedRegions(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const toggleProvincial = (id: string) => {
    setSelectedProvincial(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const toggleCommitteeGroup = (id: string) => {
    setExpandedCommittees(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const toggleCommittee = (id: string) => {
    setSelectedCommittees(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const handleRemoveItem = (id: string) => {
    // If it's a temporary category, just remove it from the temporary list
    if (id.startsWith('temp-')) {
      setTemporaryCategories(prev => prev.filter(cat => cat.id !== id));
      // Also remove from pending changes
      setPendingCustomCategoryChanges(prev => {
        const updated = new Map(prev);
        updated.delete(id);
        return updated;
      });
      // Close if this item was open
      if (openItemId === id) {
        setOpenItemId(null);
      }
    } else {
      // For existing categories, call the parent's remove handler
      onRemove(id);
    }
  };

  const handleAddItem = () => {
    // Generate a unique category name by checking existing categories
    const baseName = "New Topic";
    let categoryName = baseName;
    let counter = 1;
    
    // Check if "New Topic" exists, if so, try "New Topic 1", "New Topic 2", etc.
    const allCategoryNames = new Set([
      ...customCategories.map((cat: any) => cat.label?.toLowerCase() || ''),
      ...temporaryCategories.map((cat: any) => cat.label?.toLowerCase() || '')
    ]);
    
    while (allCategoryNames.has(categoryName.toLowerCase())) {
      categoryName = `${baseName} ${counter}`;
      counter++;
    }
    
    // Create a temporary category (not saved to database yet)
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newTempCategory = {
      id: tempId,
      label: categoryName,
      description: "Describe what you want to track...",
      isCustom: true,
      is_default: false,
      is_followed: true,
      is_active: true,
      active: false,
      count: 0,
      dbId: null, // No database ID yet
      monitoring_sources: []
    };
    
    // Add to temporary categories
    setTemporaryCategories(prev => [...prev, newTempCategory]);
    
    // Initialize pending changes for this temporary category
    trackCustomCategoryChange(tempId, {
      label: categoryName,
      description: "Describe what you want to track...",
      monitoring_sources: []
    });
      
      // Auto-expand the newly created item
        setTimeout(() => {
      setOpenItemId(tempId);
        }, 100);
  };

  // Populate location state when a category is opened for editing
  // Track the previous openItemId to detect when a NEW item is opened
  const prevOpenItemIdRef = React.useRef<string | null>(null);
  
  useEffect(() => {
    // Only reset/load when a DIFFERENT item is opened, not on every pendingChanges update
    if (openItemId !== prevOpenItemIdRef.current) {
      prevOpenItemIdRef.current = openItemId;
      
      if (openItemId) {
        const category = categories.find(cat => cat.id === openItemId) ||
                         temporaryCategories.find(cat => cat.id === openItemId);
        
        // Check if monitor_all is enabled for this category
        const pendingChanges = pendingCustomCategoryChanges.get(openItemId);
        const hasMonitorAll = pendingChanges?.monitor_all ?? category?.monitor_all ?? false;
        setMonitorAllSources(hasMonitorAll);
        
        // Check for pending monitoring sources first
        const pendingSources = pendingChanges?.monitoring_sources;
        
        if (pendingSources && pendingSources.length > 0) {
          // Use pending sources if available
          const muniIds = pendingSources.filter((src: any) => src.source_type === 'municipality').map((src: any) => String(src.source_id));
          const regionIds = pendingSources.filter((src: any) => src.source_type === 'region').map((src: any) => String(src.source_id));
          const provIds = pendingSources.filter((src: any) => src.source_type === 'provincial').map((src: any) => String(src.source_id));
          setSelectedMunicipalities(muniIds);
          setSelectedRegions(regionIds);
          setSelectedProvincial(provIds);
        } else if (category && category.monitoring_sources && !hasMonitorAll) {
          // Extract municipality IDs from saved sources
          const muniIds = category.monitoring_sources
            .filter((src: any) => src.source_type === 'municipality')
            .map((src: any) => String(src.source_id));
          
          // Extract region IDs
          const regionIds = category.monitoring_sources
            .filter((src: any) => src.source_type === 'region')
            .map((src: any) => String(src.source_id));
          
          // Extract provincial IDs
          const provIds = category.monitoring_sources
            .filter((src: any) => src.source_type === 'provincial')
            .map((src: any) => String(src.source_id));
          
          setSelectedMunicipalities(muniIds);
          setSelectedRegions(regionIds);
          setSelectedProvincial(provIds);
        } else {
          // Reset if no category or no monitoring sources (or monitor_all is enabled)
          setSelectedMunicipalities([]);
          setSelectedRegions([]);
          setSelectedProvincial([]);
        }
      } else {
        // Reset when no item is open
        setSelectedMunicipalities([]);
        setSelectedRegions([]);
        setSelectedProvincial([]);
        setMonitorAllSources(false);
      }
    }
  }, [openItemId, categories, temporaryCategories, pendingCustomCategoryChanges]);

  // Track custom category changes locally without saving
  const trackCustomCategoryChange = useCallback((id: string, updates: any) => {
    setPendingCustomCategoryChanges(prev => {
      const updated = new Map(prev);
      const existing = updated.get(id) || {};
      updated.set(id, { ...existing, ...updates });
      return updated;
    });
  }, []);

  // Update monitoring sources when location selections change
  useEffect(() => {
    if (openItemId) {
      // Check both categories and temporaryCategories for the item
      const category = categories.find(cat => cat.id === openItemId) || 
                       temporaryCategories.find(cat => cat.id === openItemId);
      if (!category) return;
      
      const monitoringSources: any[] = [];
      
      // Add selected municipalities
      selectedMunicipalities.forEach(id => {
        const muni = municipalitiesList.find((m: any) => m.id === id);
        if (muni) {
          monitoringSources.push({
            source_type: 'municipality',
            source_id: String(id),
            source_name: muni.name || muni.label
          });
        }
      });
      
      // Add selected regions
      selectedRegions.forEach(id => {
        const region = regionsList.find((r: any) => r.id === id);
        if (region) {
          monitoringSources.push({
            source_type: 'region',
            source_id: String(id),
            source_name: region.name || region.label
          });
        }
      });
      
      // Add selected provincial sources
      selectedProvincial.forEach(id => {
        const prov = provincialList.find((p: any) => p.id === id);
        if (prov) {
          monitoringSources.push({
            source_type: 'provincial',
            source_id: String(id),
            source_name: prov.name || prov.label
          });
        }
      });
      
      // Only track changes if they're different from the original
      const currentSources = category.monitoring_sources || [];
      const currentSourcesSorted = JSON.stringify(currentSources.sort((a: any, b: any) => 
        `${a.source_type}-${a.source_id}`.localeCompare(`${b.source_type}-${b.source_id}`)
      ));
      const newSourcesSorted = JSON.stringify(monitoringSources.sort((a: any, b: any) => 
        `${a.source_type}-${a.source_id}`.localeCompare(`${b.source_type}-${b.source_id}`)
      ));
      
      // Only track if actually different
      if (currentSourcesSorted !== newSourcesSorted) {
        trackCustomCategoryChange(openItemId, { monitoring_sources: monitoringSources });
      }
    }
  }, [selectedMunicipalities, selectedRegions, selectedProvincial, openItemId, municipalitiesList, regionsList, provincialList, trackCustomCategoryChange, categories, temporaryCategories]);

  const handleUpdateCategory = async (id: string, updates: any) => {
    const category = categories.find(cat => cat.id === id);
    if (!category || !category.dbId) return;

    try {
      // Build monitoring sources if locations were updated
      let monitoringSources = category.monitoring_sources || [];
      
      if (updates.monitoring_sources) {
        monitoringSources = updates.monitoring_sources;
      }

      const updateData: any = {
        display_name: updates.label !== undefined ? updates.label : category.label,
        description: updates.description !== undefined ? updates.description : category.description,
      };

      if (monitoringSources.length > 0) {
        updateData.monitoring_sources = monitoringSources;
      }

      await updateCategoryMutation.mutateAsync({
        id: category.dbId,
        data: updateData
      });

      const updatedCats = categories.map(cat => 
        cat.id === id ? { ...cat, ...updates } : cat
      );
      onReorder(updatedCats);
    } catch (error: any) {
      console.error('Failed to update topic:', error);
      let errorMessage = "Failed to update topic. Please try again.";
      if (error?.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : errorMessage;
      }
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    }
  };

  const handleSaveCustom = async () => {
    if (!newName.trim() || !keywords.trim()) {
      toast({ title: "Error", description: "Please fill in all required fields." });
      return;
    }
    
    // Build monitoring sources array in the new format
    const monitoringSources: any[] = [];
    
    // Add selected municipalities (use API data, not hardcoded constants)
    console.log('Selected municipalities:', selectedMunicipalities);
    selectedMunicipalities.forEach(id => {
      const muni = municipalitiesList.find((m: any) => m.id === id);
      console.log(`Looking for municipality with id ${id}:`, muni);
      if (muni) {
        monitoringSources.push({
          source_type: 'municipality',
          source_id: String(id), // Ensure it's a string
          source_name: muni.name || muni.label // Use name from API
        });
      }
    });
    
    // Add selected regions (use API data)
    selectedRegions.forEach(id => {
      const region = regionsList.find((r: any) => r.id === id);
      if (region) {
        monitoringSources.push({
          source_type: 'region',
          source_id: String(id), // Ensure it's a string
          source_name: region.name || region.label // Use name from API
        });
      }
    });
    
    // Add selected provincial sources (use API data)
    selectedProvincial.forEach(id => {
      const prov = provincialList.find((p: any) => p.id === id);
      if (prov) {
        monitoringSources.push({
          source_type: 'provincial',
          source_id: String(id), // Ensure it's a string
          source_name: prov.name || prov.label // Use name from API
        });
      }
    });
    
    console.log('Built monitoring sources:', monitoringSources);
    
    if (editingCategoryId) {
      // Find the category to update
      const categoryToUpdate = categories.find(cat => cat.id === editingCategoryId);
      
      if (categoryToUpdate && categoryToUpdate.dbId) {
        try {
          // Build the update data
          const updateData: any = {
            display_name: newName,
            description: keywords
          };
          
          // Only include monitoring_sources if we have any
          if (monitoringSources.length > 0) {
            updateData.monitoring_sources = monitoringSources;
          }
          
          // Debug: log what we're sending
          console.log('Updating category with data:', {
            id: categoryToUpdate.dbId,
            data: updateData
          });
          
          // Update in the database
          await updateCategoryMutation.mutateAsync({
            id: categoryToUpdate.dbId,
            data: updateData
          });
          
          // Update local state
          const updatedCategories = categories.map(cat => 
            cat.id === editingCategoryId 
              ? { 
                  ...cat, 
                  label: newName, 
                  description: keywords,
                  monitoring_sources: monitoringSources
                }
              : cat
          );
          onReorder(updatedCategories);
          
          toast({ title: "Success", description: "Custom topic updated." });
        } catch (error: any) {
          console.error('Failed to update topic:', error);
          console.error('Error response data:', error.response?.data);
          console.error('Error response status:', error.response?.status);
          
          // Handle validation errors from FastAPI
          let errorMessage = "Failed to update topic. Please try again.";
          
          if (error.response?.data?.detail) {
            if (Array.isArray(error.response.data.detail)) {
              // FastAPI validation errors come as an array
              const errors = error.response.data.detail.map((err: any) => 
                `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
              ).join(', ');
              errorMessage = errors;
              console.error('Validation errors:', error.response.data.detail);
            } else if (typeof error.response.data.detail === 'string') {
              errorMessage = error.response.data.detail;
            }
          }
          
          toast({ 
            title: "Error", 
            description: errorMessage,
            variant: "destructive"
          });
          return; // Don't close the dialog on error
        }
      }
    } else {
      // Create new category with monitoring sources
      try {
        // Validate: must have monitor_all OR at least one source
        if (!monitorAllSources && monitoringSources.length === 0) {
          toast({
            title: "Source Selection Required",
            description: `Please select at least one source to monitor, or enable "Monitor All Sources".`,
            variant: "destructive",
          });
          return;
        }
        
        const createData: any = {
          display_name: newName,
          description: keywords
        };
        
        // Include monitor_all or monitoring_sources
        if (monitorAllSources) {
          createData.monitor_all = true;
        } else if (monitoringSources.length > 0) {
          createData.monitoring_sources = monitoringSources;
        }
        
        console.log('Creating category with data:', createData);
        
        await createCategoryMutation.mutateAsync(createData);
        toast({ title: "Success", description: "Custom topic created." });
      } catch (error: any) {
        console.error('Failed to create topic:', error);
        console.error('Error response:', error.response?.data);
        
        // Handle validation errors from FastAPI
        let errorMessage = "Failed to create topic. Please try again.";
        
        if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // FastAPI validation errors come as an array
            const errors = error.response.data.detail.map((err: any) => 
              `${err.loc?.join(' > ') || 'Field'}: ${err.msg}`
            ).join(', ');
            errorMessage = errors;
            console.error('Validation errors:', error.response.data.detail);
          } else if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          }
        }
        
        toast({ 
          title: "Error", 
          description: errorMessage,
          variant: "destructive"
        });
        return;
      }
    }
    
    setNewName("");
    setKeywords("");
    setSelectedMunicipalities([]);
    setSelectedRegions([]);
    setSelectedProvincial([]);
    setSelectedCommittees([]);
    setEditingCategoryId(null);
    setView("main");
  };

  return (
    <Dialog open={open} onOpenChange={(val) => {
       onOpenChange(val);
       if (!val) {
         // Clear pending changes when dialog closes
         setPendingCategoryChanges(new Map());
         setPendingCustomCategoryChanges(new Map());
         setTemporaryCategories([]);
         setOriginalDefaultStates(new Map());
         setOriginalCustomCategories(new Set());
         setCategoryNotifications(new Map());
         setTogglingNotification(null);
         setOpenItemId(null);
         setSelectedMunicipalities([]);
         setSelectedRegions([]);
         setSelectedProvincial([]);
       }
    }}>
      <DialogContent 
        className="max-w-2xl p-0 gap-0 overflow-hidden bg-white dark:bg-neutral-900 border-none shadow-2xl max-h-[85vh] flex flex-col"
        onInteractOutside={(e) => {
          // Prevent closing when clicking outside
          e.preventDefault();
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Edit Industry Pulse Categories</DialogTitle>
          <DialogDescription>Customize your tracking categories and notification preferences</DialogDescription>
        </DialogHeader>
        {/* Header */}
        <div className="p-6 bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800 sticky top-0 z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-neutral-900 dark:text-white mb-1">Edit Industry Pulse</h2>
              <p className="text-sm text-neutral-500">Customize your tracking categories.</p>
            </div>
            {/* Save/Done Button - Always green, text changes based on whether there are changes */}
            <Button 
              onClick={hasChanges ? applyCategoryChanges : () => onOpenChange(false)}
              disabled={isSavingCategories}
              className="bg-primary hover:bg-primary/90 text-white whitespace-nowrap"
              size="sm"
            >
              {isSavingCategories ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : hasChanges ? (
                "Save"
              ) : (
                "Done"
              )}
            </Button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {/* Section 1: Default Categories */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-primary" />
                      <div>
                        <h3 className="font-semibold text-sm text-neutral-900 dark:text-white">Default Topics</h3>
                        <p className="text-xs text-neutral-500 mt-0.5">AI-powered categories curated by Cityscrape</p>
                      </div>
                    </div>
                    {priorityCount > 0 && (
                      <div className="text-xs text-neutral-500">
                        {priorityCount}/3 priorities set
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {defaultCategories.map((cat: any) => {
                      const notifyEnabled = categoryNotifications.get(cat.slug) || false;
                      const isToggling = togglingNotification === cat.slug;
                      
                      const isPriority = cat.is_priority || false;
                      const isTogglingPriority = togglingPriority === cat.slug;
                      const canSetPriority = cat.is_followed && (!isPriority || priorityCount <= 3);
                      
                      return (
                      <div key={cat.id} className="relative">
                        <button
                          type="button"
                          onClick={() => toggleDefaultCategory(cat.id)}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border transition-all text-left group min-h-[76px] cursor-pointer w-full",
                            cat.is_followed 
                              ? "bg-primary/5 border-primary/40 shadow-sm" 
                              : "bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 hover:border-neutral-300"
                          )}
                        >
                          <div className={cn(
                            "h-5 w-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors pointer-events-none",
                            cat.is_followed ? "bg-primary border-primary text-white" : "bg-white dark:bg-neutral-700 border-neutral-300"
                          )}>
                            {cat.is_followed && <Check className="h-3 w-3" />}
                          </div>
                          <span 
                            className="text-sm font-medium text-neutral-900 dark:text-white flex-1 line-clamp-2 pointer-events-none"
                            title={cat.label}
                          >
                            {cat.label}
                          </span>
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                              <Info className="h-4 w-4 text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors flex-shrink-0" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs">{cat.description || 'AI-powered monitoring for this category'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </button>
                        
                        {/* Priority Star Icon - Top right (replaces notification bell) */}
                        {cat.is_followed && (
                          <button
                            onClick={(e) => toggleCategoryPriority(cat.dbId, cat.slug, isPriority, e)}
                            disabled={isTogglingPriority || !canSetPriority}
                            className={cn(
                              "absolute top-2 right-2 p-1 rounded-full transition-all z-10",
                              isPriority 
                                ? "text-yellow-500 hover:bg-yellow-50" 
                                : priorityCount >= 3
                                  ? "text-neutral-200 cursor-not-allowed"
                                  : "text-neutral-300 hover:bg-neutral-100 hover:text-neutral-500",
                              isTogglingPriority && "opacity-50 cursor-wait"
                            )}
                            title={isPriority 
                              ? "Top 3 priority - click to remove" 
                              : priorityCount >= 3
                                ? "Maximum 3 priorities already set"
                                : "Set as Top 3 priority"}
                          >
                            {isTogglingPriority ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Star className={cn("h-3.5 w-3.5", isPriority && "fill-current")} />
                            )}
                          </button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
                  <span className="text-xs font-medium text-neutral-500 px-2">OR</span>
                  <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-700" />
                </div>

                {/* Section 2: Your Custom Categories */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Tag className="h-5 w-5 text-neutral-400" />
                      <h3 className="font-semibold text-sm text-neutral-900 dark:text-white">Your Custom Topics</h3>
                    </div>
                    <Button 
                      onClick={handleAddItem} 
                      size="icon" 
                      variant="ghost" 
                      className="h-6 w-6 text-neutral-500 hover:text-primary hover:bg-primary/10 rounded-full"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Custom Categories List */}
                  <LayoutGroup>
                    {customCategories.length > 0 ? (
                      <Reorder.Group axis="y" values={customCategories} onReorder={(newOrder) => {
                        // Combine default and reordered custom categories
                        onReorder([...defaultCategories, ...newOrder]);
                      }} className="space-y-2">
                        {customCategories.map((item) => {
                          const isOpen = item.id === openItemId;
                          return (
                            <Reorder.Item key={item.id} value={item}>
                              <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
                                <div className="p-3 flex items-center gap-3 cursor-grab active:cursor-grabbing">
                                  <GripVertical className="h-4 w-4 text-neutral-300" />
                                  <div className="flex items-center justify-between w-full pr-2">
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <span className="font-medium text-sm text-neutral-900 dark:text-white line-clamp-1">{item.label}</span>
                                      <span className="text-xs text-neutral-500 line-clamp-1">{item.description}</span>
                                    </div>
                                    
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-neutral-400 hover:text-neutral-600"
                                        onClick={() => setOpenItemId(isOpen ? null : item.id as string)}
                                      >
                                        {isOpen ? <X className="h-4 w-4" /> : <Settings2 className="h-4 w-4" />}
                                      </Button>
                                      
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-neutral-400 hover:text-red-500"
                                        onClick={() => handleRemoveItem(item.id as string)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                                
                                <AnimatePresence mode="popLayout">
                                  {isOpen && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: "auto" }}
                                      exit={{ opacity: 0, height: 0 }}
                                      className="border-t border-neutral-100 dark:border-neutral-700/50"
                                    >
                                      <div className="p-4">
                                        <Tabs defaultValue="name" className="w-full">
                                          <TabsList className="grid w-full grid-cols-3 h-9 bg-neutral-100 dark:bg-neutral-800/50">
                                            <TabsTrigger value="name" className="text-xs">Name</TabsTrigger>
                                            <TabsTrigger value="desc" className="text-xs">Description</TabsTrigger>
                                            <TabsTrigger value="loc" className="text-xs">Locations</TabsTrigger>
                                          </TabsList>
                                          
                                          <div className="mt-4 space-y-4">
                                            <TabsContent value="name" className="mt-0 space-y-3">
                                              <label className="text-xs text-neutral-500 font-medium">Topic Name</label>
                                              <Input 
                                                value={pendingCustomCategoryChanges.get(item.id as string)?.label ?? item.label} 
                                                onChange={(e) => trackCustomCategoryChange(item.id as string, { label: e.target.value })}
                                                className="bg-white dark:bg-neutral-900"
                                              />
                                            </TabsContent>
                                            
                                            <TabsContent value="desc" className="mt-0 space-y-3">
                                              <label className="text-xs text-neutral-500 font-medium">What to Monitor</label>
                                              <Textarea 
                                                value={pendingCustomCategoryChanges.get(item.id as string)?.description ?? item.description} 
                                                onChange={(e) => trackCustomCategoryChange(item.id as string, { description: e.target.value })}
                                                className="min-h-[100px] resize-none bg-white dark:bg-neutral-900"
                                                placeholder="Enter keywords or description..."
                                              />
                                            </TabsContent>
                                            
                                            <TabsContent value="loc" className="mt-0 space-y-3">
                                              <label className="text-xs text-neutral-500 font-medium">Target Locations</label>
                                              <p className="text-xs text-neutral-400 mb-3">Select specific locations to monitor, or enable "Monitor All" to track all sources.</p>
                                              
                                              {/* Monitor All Toggle */}
                                              <div className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700 mb-3">
                                                <div className="flex flex-col">
                                                  <span className="text-sm font-medium text-neutral-900 dark:text-white">Monitor All Sources</span>
                                                  <span className="text-xs text-neutral-500">Track all municipalities, regions, and provincial sources</span>
                                                </div>
                                                <Switch
                                                  checked={pendingCustomCategoryChanges.get(item.id as string)?.monitor_all ?? monitorAllSources}
                                                  onCheckedChange={(checked) => {
                                                    setMonitorAllSources(checked);
                                                    trackCustomCategoryChange(item.id as string, { monitor_all: checked });
                                                    // Clear individual selections when monitor all is enabled
                                                    if (checked) {
                                                      setSelectedMunicipalities([]);
                                                      setSelectedRegions([]);
                                                      setSelectedProvincial([]);
                                                    }
                                                  }}
                                                />
                                              </div>
                                              
                                              {/* Individual Source Selection - disabled when Monitor All is checked */}
                                              <div className={`space-y-2 ${(pendingCustomCategoryChanges.get(item.id as string)?.monitor_all ?? monitorAllSources) ? 'opacity-50 pointer-events-none' : ''}`}>
                                                {/* Municipalities Dropdown */}
                                                {municipalitiesList.length > 0 && (
                                                  <DropdownMenu open={municipalitiesDropdownOpen} onOpenChange={setMunicipalitiesDropdownOpen}>
                                                    <DropdownMenuTrigger asChild>
                                                      <Button
                                                        variant="outline"
                                                        className="w-full justify-between text-left font-normal h-9 text-xs"
                                                      >
                                                        <span>
                                                          Municipalities
                                                          {selectedMunicipalities.length > 0 && (
                                                            <span className="ml-2 text-primary">
                                                              ({selectedMunicipalities.length} selected)
                                                            </span>
                                                          )}
                                                        </span>
                                                        <ChevronDown className="h-3 w-3 opacity-50" />
                                                      </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent className="w-64 max-h-64 overflow-y-auto">
                                                      <DropdownMenuLabel>Select Municipalities</DropdownMenuLabel>
                                                      <DropdownMenuSeparator />
                                                      {municipalitiesList.map((muni: any) => (
                                                        <DropdownMenuCheckboxItem
                                                          key={muni.id}
                                                          checked={selectedMunicipalities.includes(muni.id)}
                                                          onCheckedChange={() => toggleMunicipality(muni.id)}
                                                          onSelect={(e) => e.preventDefault()}
                                                        >
                                                          {muni.label}
                                                        </DropdownMenuCheckboxItem>
                                                      ))}
                                                    </DropdownMenuContent>
                                                  </DropdownMenu>
                                                )}

                                                {/* Regions Dropdown */}
                                                {regionsList.length > 0 && (
                                                  <DropdownMenu open={regionsDropdownOpen} onOpenChange={setRegionsDropdownOpen}>
                                                    <DropdownMenuTrigger asChild>
                                                      <Button
                                                        variant="outline"
                                                        className="w-full justify-between text-left font-normal h-9 text-xs"
                                                      >
                                                        <span>
                                                          Regions
                                                          {selectedRegions.length > 0 && (
                                                            <span className="ml-2 text-primary">
                                                              ({selectedRegions.length} selected)
                                                            </span>
                                                          )}
                                                        </span>
                                                        <ChevronDown className="h-3 w-3 opacity-50" />
                                                      </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent className="w-64 max-h-64 overflow-y-auto">
                                                      <DropdownMenuLabel>Select Regions</DropdownMenuLabel>
                                                      <DropdownMenuSeparator />
                                                      {regionsList.map((region: any) => (
                                                        <DropdownMenuCheckboxItem
                                                          key={region.id}
                                                          checked={selectedRegions.includes(region.id)}
                                                          onCheckedChange={() => toggleRegion(region.id)}
                                                          onSelect={(e) => e.preventDefault()}
                                                        >
                                                          {region.label}
                                                        </DropdownMenuCheckboxItem>
                                                      ))}
                                                    </DropdownMenuContent>
                                                  </DropdownMenu>
                                                )}

                                                {/* Provincial Sources Dropdown */}
                                                {provincialList.length > 0 && (
                                                  <DropdownMenu open={provincialDropdownOpen} onOpenChange={setProvincialDropdownOpen}>
                                                    <DropdownMenuTrigger asChild>
                                                      <Button
                                                        variant="outline"
                                                        className="w-full justify-between text-left font-normal h-9 text-xs"
                                                      >
                                                        <span>
                                                          Provincial Sources
                                                          {selectedProvincial.length > 0 && (
                                                            <span className="ml-2 text-primary">
                                                              ({selectedProvincial.length} selected)
                                                            </span>
                                                          )}
                                                        </span>
                                                        <ChevronDown className="h-3 w-3 opacity-50" />
                                                      </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent className="w-64 max-h-64 overflow-y-auto">
                                                      <DropdownMenuLabel>Select Provincial Sources</DropdownMenuLabel>
                                                      <DropdownMenuSeparator />
                                                      {provincialList.map((prov: any) => (
                                                        <DropdownMenuCheckboxItem
                                                          key={prov.id}
                                                          checked={selectedProvincial.includes(prov.id)}
                                                          onCheckedChange={() => toggleProvincial(prov.id)}
                                                          onSelect={(e) => e.preventDefault()}
                                                        >
                                                          {prov.label}
                                                        </DropdownMenuCheckboxItem>
                                                      ))}
                                                    </DropdownMenuContent>
                                                  </DropdownMenu>
                                                )}
                                              </div>
                                            </TabsContent>
                                          </div>
                                        </Tabs>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </Reorder.Item>
                          );
                        })}
                      </Reorder.Group>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl">
                        <p className="text-sm text-neutral-500">No custom topics yet.</p>
                        <Button onClick={handleAddItem} variant="link" className="text-primary">
                          Create one now
                        </Button>
                      </div>
                    )}
                  </LayoutGroup>
                </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}